/**
 * KML pin icons and area-code matching.
 *
 * Activity properties store the code description in `.value` (ACTVCODE.actv_code_name).
 * Pin titles are matched to that description, never to short_name.
 * Exact text wins. A normalized comparison is only the fallback.
 */

export const KML_PADDLE_ICONS = [
	'https://maps.google.com/mapfiles/kml/paddle/red-circle.png',
	'https://maps.google.com/mapfiles/kml/paddle/blu-circle.png',
	'https://maps.google.com/mapfiles/kml/paddle/grn-circle.png',
	'https://maps.google.com/mapfiles/kml/paddle/ylw-circle.png',
	'https://maps.google.com/mapfiles/kml/paddle/wht-circle.png',
	'https://maps.google.com/mapfiles/kml/paddle/purple-circle.png',
	'https://maps.google.com/mapfiles/kml/paddle/orange-circle.png',
	'https://maps.google.com/mapfiles/kml/paddle/pink-circle.png'
];

/**
 * True when a browser cannot load this IconStyle href.
 * Local drive paths, file:// URLs, and relative paths are unloadable.
 * Same-origin absolute paths and http(s) URLs are left alone; a later
 * image error handler covers http(s) URLs that 404.
 */
export function isUnloadableIconHref(href) {
	if (href == null) {
		return false;
	}
	const value = String(href).trim();
	if (value === '') {
		return false;
	}
	if (/^file:/i.test(value)) {
		return true;
	}
	if (/^[a-zA-Z]:[\\/]/.test(value)) {
		return true;
	}
	if (value.startsWith('\\\\')) {
		return true;
	}
	if (/^(https?:)?\/\//i.test(value)) {
		return false;
	}
	if (/^data:/i.test(value)) {
		return false;
	}
	if (value.startsWith('/')) {
		return false;
	}
	return true;
}

export function fallbackPaddleHref(styleId) {
	const key = String(styleId || 'pin');
	let hash = 0;
	for (let i = 0; i < key.length; i++) {
		hash = (hash + key.charCodeAt(i) * (i + 1)) % KML_PADDLE_ICONS.length;
	}
	return KML_PADDLE_ICONS[hash];
}

/**
 * Replace an unloadable href with a standard paddle icon.
 * Empty hrefs stay empty so Google Maps draws its default marker.
 */
export function resolveKmlIconHref(href, styleId) {
	if (href == null || String(href).trim() === '') {
		return href;
	}
	if (!isUnloadableIconHref(href)) {
		return href;
	}
	return fallbackPaddleHref(styleId);
}

export function localIconHrefsFromKml(kmlText) {
	const hrefs = [];
	const seen = new Set();
	const re = /<href>\s*([^<]*?)\s*<\/href>/gi;
	let match = re.exec(String(kmlText || ''));
	while (match) {
		const href = match[1].trim();
		if (isUnloadableIconHref(href) && !seen.has(href)) {
			seen.add(href);
			hrefs.push(href);
		}
		match = re.exec(String(kmlText || ''));
	}
	return hrefs;
}

/**
 * Escape a placemark name for an info window. The KML description is HTML
 * from Google Earth and must not be inserted.
 */
export function escapeHtmlText(value) {
	return String(value == null ? '' : value)
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;');
}

export function placemarkInfoWindowHtml(name) {
	return '<div class="geoxml3_infowindow"><h3>' + escapeHtmlText(name) + '</h3></div>';
}

export function normalizePinLabel(value) {
	let text = String(value == null ? '' : value);
	if (typeof text.normalize === 'function') {
		text = text.normalize('NFKC');
	}
	text = text
		.replace(/[\u2018\u2019\u201A\u2032\u0092\uFF07]/g, "'")
		.replace(/[\u201C\u201D\u201E\u00AB\u00BB]/g, '"')
		.replace(/[\u2010\u2011\u2012\u2013\u2014\u2212]/g, '-');
	text = text.replace(/(\d)\s*([kK][vV])\b/g, '$1 kV');
	text = text.replace(/\s+/g, ' ').trim().toLowerCase();
	return text;
}

/**
 * Attach activities to placemarks.
 * `areaProperty` is the activity-code type id. The joined value is the
 * description (actv_code_name), which getDatasetInfo stores on `.value`.
 */
export function bindActivitiesToPlacemarks(placemarkItems, projectActivities, areaProperty) {
	Object.keys(placemarkItems).forEach(key => {
		placemarkItems[key].activities = [];
	});
	if (!projectActivities || !areaProperty) {
		return;
	}
	const byNorm = new Map();
	Object.keys(placemarkItems).forEach(key => {
		const normalized = normalizePinLabel(key);
		if (!byNorm.has(normalized)) {
			byNorm.set(normalized, []);
		}
		byNorm.get(normalized).push(key);
	});
	projectActivities.forEach(activity => {
		const properties = activity && activity.properties;
		if (!properties || !(`_${areaProperty}` in properties)) {
			return;
		}
		const title = properties[`_${areaProperty}`].value;
		if (title == null || title === '') {
			return;
		}
		if (Object.prototype.hasOwnProperty.call(placemarkItems, title)) {
			placemarkItems[title].activities.push(activity);
			return;
		}
		const hits = byNorm.get(normalizePinLabel(title)) || [];
		hits.forEach(key => {
			placemarkItems[key].activities.push(activity);
		});
	});
}

/**
 * Compare pin titles to area-code descriptions.
 * Exact match first, then the normalized label.
 */
export function summarizePinCodeMatch(pinTitles, codeValues) {
	const pins = (pinTitles || []).filter(pin => pin != null && String(pin) !== '');
	const codes = (codeValues || []).filter(code => code != null && String(code) !== '').map(code => String(code));
	const uniquePins = [];
	const seenPins = new Set();
	pins.forEach(pin => {
		const text = String(pin);
		if (!seenPins.has(text)) {
			seenPins.add(text);
			uniquePins.push(text);
		}
	});
	const matchedCodes = new Set();
	const matchedPins = new Set();
	const pinsByNorm = new Map();
	uniquePins.forEach(pin => {
		const normalized = normalizePinLabel(pin);
		if (!pinsByNorm.has(normalized)) {
			pinsByNorm.set(normalized, []);
		}
		pinsByNorm.get(normalized).push(pin);
	});
	codes.forEach(code => {
		if (seenPins.has(code)) {
			matchedCodes.add(code);
			matchedPins.add(code);
		}
	});
	codes.forEach(code => {
		if (matchedCodes.has(code)) {
			return;
		}
		const hits = pinsByNorm.get(normalizePinLabel(code)) || [];
		if (!hits.length) {
			return;
		}
		matchedCodes.add(code);
		hits.forEach(pin => matchedPins.add(pin));
	});
	return {
		pinCount: pins.length,
		uniquePinCount: uniquePins.length,
		codeCount: codes.length,
		matchedPinCount: matchedPins.size,
		matchedCodeCount: matchedCodes.size,
		unmatchedPins: uniquePins.filter(pin => !matchedPins.has(pin)).sort(),
		unmatchedCodes: codes.filter(code => !matchedCodes.has(code)).sort()
	};
}

/**
 * How a placemark should draw after matching.
 * Unmatched pins stay visible and dimmed. A date filter hides a pin only
 * when it has activities and none of them fall in the current range.
 * With no legend group, every pin stays visible.
 */
export function placemarkDisplayState({
	activities,
	projectMapGroupBy,
	filteredActivitiesMap,
	hasActivityCodeType
}) {
	const bound = activities || [];
	if (!hasActivityCodeType || !projectMapGroupBy) {
		return bound.length ? 'shown' : 'dimmed';
	}
	const filtered = bound.filter(activity => {
		return activity
			&& activity.activityId in (filteredActivitiesMap || {})
			&& activity.properties
			&& (`_${projectMapGroupBy}` in activity.properties);
	});
	if (filtered.length) {
		return 'colored';
	}
	if (!bound.length) {
		return 'dimmed';
	}
	return 'hidden';
}
